import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * DEFTER — panel istemcisi.
 *
 * ⚠ BURADA "GERİ AÇ" YOK ve olmayacak. Atılmış fiş düzeltilmez, TERS KAYITLA
 * giderilir. Alış ekranında `reopen` vardır çünkü orada kayıt tedarikçinin
 * belgesinin bir kopyasıdır; yevmiye defteri belgenin KENDİSİDİR.
 *
 * ⚠ HESAP PLANI SALT OKUNUR — hesap açma/silme ucu bilerek yok. Yeni hesap bir
 * MIGRATION ile gelir, böylece her kurulumda aynı olur.
 */

export type JournalEntryStatus = 'DRAFT' | 'POSTED' | 'REVERSED';

export const STATUS_LABELS: Record<JournalEntryStatus, string> = {
  DRAFT: 'Taslak',
  POSTED: 'Atıldı',
  REVERSED: 'Ters çevrildi',
};

export interface Account {
  code: string;
  name: { nl: string; fr: string; tr: string };
  accountClass: number;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentCode: string | null;
  active: boolean;
}

export interface Journal {
  code: string;
  name: { nl: string; fr: string; tr: string };
  type: string;
  active: boolean;
}

export interface FiscalYear {
  year: number;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSING' | 'CLOSED';
}

export interface JournalEntryLine {
  id?: string;
  lineNo?: number;
  accountCode: string;
  debit: string;
  credit: string;
  description?: string | null;
  vatGrid?: string | null;
}

export interface JournalEntry {
  id: string;
  journalCode: string;
  status: JournalEntryStatus;
  entryNumber: string | null;
  entryDate: string;
  fiscalYear: number;
  description: string;
  sourceType: string;
  reversesEntryId: string | null;
  reversedByEntryId: string | null;
  postedAt: string | null;
  lines?: JournalEntryLine[];
}

export interface ListEntriesParams {
  page?: number;
  pageSize?: number;
  status?: JournalEntryStatus;
  journalCode?: string;
  fiscalYear?: number;
  q?: string;
}

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    // ⚠ Hesap planı yerleşiktir ve migration ile değişir; bir oturum boyunca
    //   yeniden çekmenin anlamı yok.
    staleTime: Infinity,
    queryFn: () => api.get<Account[]>('/accounting/accounts'),
  });
}

export function useJournals() {
  return useQuery({
    queryKey: ['journals'],
    staleTime: Infinity,
    queryFn: () => api.get<Journal[]>('/accounting/journals'),
  });
}

export function useFiscalYears() {
  return useQuery({
    queryKey: ['fiscal-years'],
    queryFn: () => api.get<FiscalYear[]>('/accounting/fiscal-years'),
  });
}

export function useOpenFiscalYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) => api.post<FiscalYear>('/accounting/fiscal-years', { year }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['fiscal-years'] }),
  });
}

export function useJournalEntries(params: ListEntriesParams) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  if (params.journalCode) qs.set('journalCode', params.journalCode);
  if (params.fiscalYear) qs.set('fiscalYear', String(params.fiscalYear));
  if (params.q) qs.set('q', params.q);

  return useQuery({
    queryKey: ['journal-entries', params],
    queryFn: () =>
      api.get<{ items: JournalEntry[]; total: number }>(`/accounting/entries?${qs.toString()}`),
  });
}

export function useJournalEntry(id: string | undefined) {
  return useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => api.get<JournalEntry>(`/accounting/entries/${id}`),
    enabled: Boolean(id),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['journal-entries'] });
  void qc.invalidateQueries({ queryKey: ['journal-entry'] });
}

export function useCreateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<JournalEntry>('/accounting/entries', body),
    onSuccess: () => invalidate(qc),
  });
}

export function usePostJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<JournalEntry>(`/accounting/entries/${id}/post`, {}),
    onSuccess: () => invalidate(qc),
  });
}

export function useReverseJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entryDate }: { id: string; entryDate: string }) =>
      api.post<JournalEntry>(`/accounting/entries/${id}/reverse`, { entryDate }),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/accounting/entries/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Deftere girmemiş belge sayıları.
 *
 * ⚠ BU SAYAÇ BİR TELAFİDİR. Kayıt, belge işleminden AYRI bir adım (fatura
 * kesme transaction'ı sayaç kilidi tutuyor; oraya ikinci bir sayaç sokmak
 * deadlock riski açar). Ayrı olduğu için unutulabilir — sayaç o riskin
 * karşılığı, tıpkı giderlerin "fişi eksik" süzgeci gibi.
 */
export interface UnpostedCounts {
  sales: number;
  purchases: number;
  expenses: number;
  /** Deftere girmemiş tahsilatlar (F2) — alacak hesabı onlarsız kapanmaz. */
  payments: number;
  /** Mahsubu yazılmamış gönderilmiş periyodik beyanlar (F5). */
  vatSettlements: number;
  /** Deftere girmemiş devlete KDV ödemeleri (F5). */
  vatPayments: number;
}

/** Tek toplam — pano ve defter sayfası aynı toplamı kendileri saymasın. */
export function unpostedTotal(n: UnpostedCounts | undefined): number {
  return (
    (n?.sales ?? 0) +
    (n?.purchases ?? 0) +
    (n?.expenses ?? 0) +
    (n?.payments ?? 0) +
    (n?.vatSettlements ?? 0) +
    (n?.vatPayments ?? 0)
  );
}

/**
 * ⚠ SORGU TANIMI HOOK'TAN AYRI DURUYOR çünkü pano aynı sayacı FARKLI bir
 * koşulla soruyor (`enabled`: defter alanı izni). Hook'u orada çağırıp
 * seçenekleri ezmek, anahtar ile URL'in iki dosyada kopyalanması demekti;
 * ayrıştıkları gün pano ile defter sayfası aynı sayı için iki ayrı önbellek
 * tutardı ve biri diğerinin tazelemesini görmezdi.
 */
export function unpostedQuery() {
  return {
    queryKey: ['unposted'] as const,
    queryFn: () => api.get<UnpostedCounts>('/accounting/unposted'),
  };
}

export function useUnposted() {
  return useQuery(unpostedQuery());
}

export type PostableSource =
  | 'SALES_INVOICE'
  | 'PURCHASE_INVOICE'
  | 'EXPENSE'
  | 'SALES_PAYMENT'
  | 'VAT_SETTLEMENT'
  | 'VAT_PAYMENT';

/** Kaynağın atılmış fişi ya da `null` (F3 ucu). */
export function useSourceEntry(sourceType: PostableSource, sourceId: string, enabled = true) {
  return useQuery({
    queryKey: ['source-entry', sourceType, sourceId],
    queryFn: () =>
      api.get<JournalEntry | null>(
        `/accounting/entries/source?sourceType=${sourceType}&sourceId=${sourceId}`,
      ),
    enabled,
  });
}

export function usePostDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      sourceType: PostableSource;
      sourceId: string;
      moneyAccountCode?: string;
    }) => api.post<JournalEntry>('/accounting/post-document', body),
    onSuccess: () => {
      invalidate(qc);
      void qc.invalidateQueries({ queryKey: ['unposted'] });
    },
  });
}

// ─────────────────────────────────────────────────── mizan ve defter-i kebir

/**
 * ⚠ MİZANDA `POSTED` **VE** `REVERSED` fişler vardır; yalnız `DRAFT` yoktur.
 * `REVERSED` "geçersiz" demez — karşısına ters kayıt atılmış demektir ve ters
 * kaydın kendisi `POSTED`tır; ikisi birlikte sıfırlanır. Yalnız `POSTED`
 * süzülseydi mizan tam ters kayıt tutarı kadar kayar, üstelik yine DENGELİ
 * görünürdü.
 */
export interface TrialBalanceRow {
  accountCode: string;
  name: { nl: string; fr: string; tr: string };
  accountClass: number;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  debit: string;
  credit: string;
  balance: string;
}

export interface TrialBalance {
  fiscalYear: number;
  asOf: string | null;
  rows: TrialBalanceRow[];
  totalDebit: string;
  totalCredit: string;
  /** ⚠ `false` ise defterde bir arıza var; rapor bunu SÖYLEMEK zorunda. */
  balanced: boolean;
}

export interface LedgerMovement {
  entryId: string;
  entryNumber: string | null;
  entryDate: string;
  journalCode: string;
  status: string;
  description: string;
  lineDescription: string | null;
  debit: string;
  credit: string;
  runningBalance: string;
}

export interface AccountLedger {
  accountCode: string;
  name: { nl: string; fr: string; tr: string };
  fiscalYear: number;
  movements: LedgerMovement[];
  totalDebit: string;
  totalCredit: string;
  closingBalance: string;
}

export function useTrialBalance(fiscalYear: number | null, asOf?: string) {
  return useQuery({
    queryKey: ['trial-balance', fiscalYear, asOf ?? null],
    enabled: fiscalYear !== null,
    queryFn: () => {
      const qs = new URLSearchParams({ fiscalYear: String(fiscalYear) });
      if (asOf) qs.set('asOf', asOf);
      return api.get<TrialBalance>(`/accounting/trial-balance?${qs.toString()}`);
    },
  });
}

export function useAccountLedger(accountCode: string | null, fiscalYear: number | null) {
  return useQuery({
    queryKey: ['account-ledger', accountCode, fiscalYear],
    enabled: accountCode !== null && fiscalYear !== null,
    queryFn: () =>
      api.get<AccountLedger>(`/accounting/ledger/${accountCode}?fiscalYear=${String(fiscalYear)}`),
  });
}

/**
 * AÇILIŞ FİŞİ.
 *
 * ⚠ TARİH GÖNDERİLMİYOR: açılış tanımı gereği yılın İLK GÜNÜDÜR. Sunucu onu
 * kendisi koyuyor; serbest bir tarih, yıl içindeki raporların bir kısmını
 * açılışsız gösterirdi.
 */
export function useOpeningBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      fiscalYear: number;
      lines: Array<{ accountCode: string; debit?: string; credit?: string }>;
    }) => api.post<JournalEntry>('/accounting/opening-balance', body),
    onSuccess: () => {
      invalidate(qc);
      void qc.invalidateQueries({ queryKey: ['trial-balance'] });
    },
  });
}

/**
 * YIL SONU KAPANIŞI.
 *
 * ⚠ ÜÇ AYRI İSTEK, ÜÇ AYRI KARAR: hazırlık (ne eksik), önizleme (ne
 * yazılacak), kapatma (yaz). Panelde tek düğmeye indirgemek, operatörün
 * defterine ne yazıldığını ancak yazıldıktan sonra görmesi demekti.
 */
export interface ClosingIssue {
  code: string;
  blocking: boolean;
  count?: number;
  detail?: string;
}

export interface ClosingReadiness {
  fiscalYear: number;
  status: 'OPEN' | 'CLOSING' | 'CLOSED' | 'NOT_OPENED';
  closable: boolean;
  issues: ClosingIssue[];
  closingEntryId: string | null;
  closingEntryNumber: string | null;
  result: string;
  income: string;
  expense: string;
}

export interface ClosingPreview {
  journalCode: string;
  entryDate: string;
  description: string;
  lines: Array<{ accountCode: string; debit: string; credit: string; description: string }>;
  expenseCents: number;
  incomeCents: number;
  resultCents: number;
}

export function useClosingReadiness(fiscalYear: number | null) {
  return useQuery({
    queryKey: ['year-end', fiscalYear],
    enabled: fiscalYear !== null,
    queryFn: () =>
      api.get<ClosingReadiness>(`/accounting/year-end/${String(fiscalYear)}/readiness`),
  });
}

export function useClosingPreview(fiscalYear: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['year-end', fiscalYear, 'preview'],
    enabled: enabled && fiscalYear !== null,
    retry: false,
    queryFn: () => api.get<ClosingPreview>(`/accounting/year-end/${String(fiscalYear)}/preview`),
  });
}

function invalidateYearEnd(qc: ReturnType<typeof useQueryClient>): void {
  invalidate(qc);
  void qc.invalidateQueries({ queryKey: ['year-end'] });
  void qc.invalidateQueries({ queryKey: ['fiscal-years'] });
  void qc.invalidateQueries({ queryKey: ['trial-balance'] });
}

export function useCloseYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscalYear: number) =>
      api.post<JournalEntry>(`/accounting/year-end/${String(fiscalYear)}/close`),
    onSuccess: () => invalidateYearEnd(qc),
  });
}

export function useReopenYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscalYear: number) =>
      api.post<JournalEntry>(`/accounting/year-end/${String(fiscalYear)}/reopen`),
    onSuccess: () => invalidateYearEnd(qc),
  });
}

/**
 * Kapanış engelleri — her biri NE YAPILACAĞINI söyler.
 *
 * ⚠ "ENGEL" İLE "UYARI" AYRI: eksik bir defteri kapatmak eksikliği kalıcı
 * kılar (engel); verilmemiş bir KDV beyanı ise defterin kapanmasından ayrı bir
 * borçtur (uyarı) ve kapanışı kilitlemesi için bir sebep yok.
 */
export const CLOSING_ISSUES: Record<string, string> = {
  YEAR_NOT_ENDED: 'Yıl henüz bitmedi. Kapanış, 31 Aralık’tan önce yapılamaz.',
  ALREADY_CLOSED: 'Bu yıl zaten kapatılmış.',
  DRAFT_ENTRIES:
    'Bu yıla ait TASLAK yevmiye fişi var. Taslak, deftere girmemiş bir kayıttır: yıl onunla kapatılırsa kayıt kalıcı olarak eksik kalır. Fişleri atın ya da silin.',
  UNPOSTED_DOCUMENTS:
    'Deftere kaydedilmemiş belge var. Düzenlenmiş fatura ve onaylanmış gider yasal olarak o yıla aittir; kaydedilmeden kapatılan yıl, eksik bir gelir tablosu üretir.',
  DEPRECIATION_NOT_POSTED:
    'Bu yılın amortisman fişi atılmamış. Amortismansız kapatılan yıl kârı OLDUĞUNDAN YÜKSEK gösterir — ve o rakam beyana girer. Duran varlıklar ekranından fişi atın.',
  TRIAL_BALANCE_UNBALANCED:
    '⚠ Mizan denk değil. Bu bir veri arızasıdır: mizan tanımı gereği denk olmak zorundadır. Kapanış yapılmadan önce sebebi bulunmalı.',
  VAT_RETURNS_MISSING:
    'Bu yıl için gönderilmiş KDV beyanı kaydı görünmüyor. Defterin kapanması beyan yükümlülüğünü ortadan kaldırmaz; beyanları verdiyseniz kayıtlarını da girin.',
};

/**
 * ENVANTER DEFTERİ (inventarisboek) — zorunlu yıllık defter.
 *
 * ⚠ RAKAMLAR SUNUCUDA ÜRETİLİR VE DONDURULUR. Panel hiçbir tutar göndermez:
 * gönderebilseydi "beyan edilen" ile "defterde olan" ayrışırdı.
 */
export interface InventorySnapshot {
  id: string;
  fiscalYear: number;
  takenOn: string;
  status: 'FINAL' | 'DISCARDED';
  content: InventoryContent;
  totalAssets: string;
  totalLiabilities: string;
  equity: string;
  notes: string | null;
  discardReason: string | null;
  createdAt: string;
}

export interface InventoryLine {
  label: string;
  reference: string | null;
  amount: string;
  detail?: string;
}

export interface InventoryContent {
  fixedAssets: {
    lines: Array<{
      label: string;
      acquisitionDate: string;
      cost: string;
      accumulated: string;
      bookValue: string;
    }>;
    totalCost: string;
    totalAccumulated: string;
    totalBookValue: string;
  };
  /**
   * ⚠ Sınıf 2–3'ün ALACAK bakiyelileri (birikmiş amortisman) aktifte NEGATİF
   * durur: varlığın düzelticisidir, borç değildir.
   */
  ledger: { assets: InventoryLine[]; liabilities: InventoryLine[]; equity: InventoryLine[] };
  /** Kapanış yapılmamışsa dönem sonucu — henüz sermayeye taşınmamıştır. */
  unclosedResult: string;
  workInProgress: {
    entries: number;
    valuedEntries: number;
    missingCostRate: number;
    hours: string;
    value: string;
  };
  exclusions: string[];
}

export interface InventoryPreview {
  fiscalYear: number;
  takenOn: string;
  content: InventoryContent;
  totalAssets: string;
  totalLiabilities: string;
  equity: string;
  unclosedResult: string;
  /**
   * Aktif − pasif = öz kaynak + kapanmamış sonuç. Tutmuyorsa envanter değil,
   * DEFTER bozuktur.
   */
  balanced: boolean;
}

export function useInventoryPreview(fiscalYear: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['inventory', fiscalYear, 'preview'],
    enabled: enabled && fiscalYear !== null,
    retry: false,
    queryFn: () => api.get<InventoryPreview>(`/accounting/inventory/${String(fiscalYear)}/preview`),
  });
}

export function useInventories() {
  return useQuery({
    queryKey: ['inventory'],
    queryFn: () => api.get<InventorySnapshot[]>('/accounting/inventory'),
  });
}

export function useTakeInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fiscalYear, notes }: { fiscalYear: number; notes?: string }) =>
      api.post<InventorySnapshot>(`/accounting/inventory/${String(fiscalYear)}`, { notes }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inventory'] }),
  });
}

export function useDiscardInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fiscalYear, reason }: { fiscalYear: number; reason: string }) =>
      api.post<InventorySnapshot>(`/accounting/inventory/${String(fiscalYear)}/discard`, {
        reason,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inventory'] }),
  });
}

/**
 * MUHASEBECİ / DENETİM PAKETİ — bir yılın defterleri ve belgeleri, tek ZIP.
 *
 * ⚠ BELGELER VARSAYILAN OLARAK İÇERİDE. Denetim isteyen kişi belgeleri de
 * ister; "unutulmuş" bir bayrak yüzünden eksik giden paket, eksik olduğu ancak
 * karşı tarafta anlaşılan bir pakettir.
 */
export function downloadAccountantPackage(fiscalYear: number, includeDocuments = true) {
  const qs = includeDocuments ? '' : '?documents=0';
  return api.file(`/accounting/package/${String(fiscalYear)}${qs}`);
}
